import { useEffect, useRef } from 'react';

import { supabase } from '../lib/supabase';
import { useAppDispatch } from '../store/store';
import { artistsActions } from '../store/slices/artists';
import type { ArtistContent } from '../interfaces/maestra';

// AS TELAS DOS MÓDULOS AO VIVO (24/09/2026).
//
// Nenhuma tela de módulo escutava o banco: Plano de Ação, Planejamento, Agenda, Equipe e Músicas
// carregavam uma vez e só viam o que ELAS mesmas gravavam. O que chegava por outro caminho (a Nyta
// criando uma tarefa pelo servidor, outra pessoa da equipe editando, o app do celular) só aparecia
// ao recarregar o navegador. O tempo real já estava ligado nas tabelas; faltava se inscrever.
//
// Dois ganchos:
//   - `useArtistaAoVivo`: o `content` do perfil aberto (plano, estratégias, planejamento) chega
//     pronto no evento e entra direto no store. Montado UMA vez, na casca de cada app.
//   - `useTabelaAoVivo`: para as listas que moram em tabela própria (agenda, equipe, músicas), o
//     evento só avisa que mudou e a tela recarrega do jeito dela — a lista continua vindo da mesma
//     consulta, com a mesma regra, e não de um remendo feito com o payload.
//
// A RLS vale para o tempo real: cada pessoa só recebe eventos das linhas que pode ler.
//
// Sem tempo real (cliente sem `channel`, ou o serviço fora), a tela funciona como antes: carrega
// ao abrir e mostra o que ela mesma grava. Ao vivo é um ganho, nunca uma dependência.
const temTempoReal = (): boolean => typeof (supabase as { channel?: unknown }).channel === 'function';

/** O conteúdo do perfil aberto, ao vivo. */
export function useArtistaAoVivo(artistId: string | null | undefined): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!artistId || !temTempoReal()) return undefined;
    const canal = supabase
      .channel(`artista-ao-vivo:${artistId}:${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes' as never,
        { event: 'UPDATE', schema: 'public', table: 'artists', filter: `id=eq.${artistId}` },
        (payload: { new?: { id?: string; content?: ArtistContent } }) => {
          const linha = payload.new;
          if (linha?.id && linha.content) {
            dispatch(artistsActions.setArtistContentLocal({ id: linha.id, content: linha.content }));
          }
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(canal); };
  }, [artistId, dispatch]);
}

/**
 * Chama `aoMudar` quando alguma linha da tabela, filtrada pelo perfil, muda. Várias mudanças em
 * sequência (a Nyta criando três eventos, um lote da agenda do plano) viram UMA recarga: espera
 * `espera` ms depois do último evento.
 */
export function useTabelaAoVivo(
  tabela: string,
  artistId: string | null | undefined,
  aoMudar: () => void,
  espera = 400,
): void {
  const ultimo = useRef(aoMudar);
  ultimo.current = aoMudar;

  useEffect(() => {
    if (!artistId || !temTempoReal()) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const avisar = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => ultimo.current(), espera);
    };
    // ⚠️ A EXCLUSÃO NÃO SE FILTRA. Com RLS, o evento de DELETE só traz a chave primária da linha
    // apagada, e o filtro por `artist_id` nunca casaria: apagar pela Nyta ou em outro aparelho
    // ficaria na tela. Por isso o DELETE vem sem filtro, só como aviso para reler — não vaza nada
    // (é só o id) e exclusão é rara. Criar e alterar seguem filtrados pelo perfil.
    const canal = supabase
      .channel(`${tabela}-ao-vivo:${artistId}:${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes' as never,
        { event: 'INSERT', schema: 'public', table: tabela, filter: `artist_id=eq.${artistId}` },
        avisar,
      )
      .on(
        'postgres_changes' as never,
        { event: 'UPDATE', schema: 'public', table: tabela, filter: `artist_id=eq.${artistId}` },
        avisar,
      )
      .on('postgres_changes' as never, { event: 'DELETE', schema: 'public', table: tabela }, avisar)
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(canal);
    };
  }, [tabela, artistId, espera]);
}

/**
 * Quem é MEMBRO do perfil aberto fica sabendo na hora quando sai da equipe (QA da Equipe, 26/09).
 * Antes, removido pelo titular, a pessoa seguia com o perfil na tela e cada gravação falhava em
 * silêncio (RLS negando um UPDATE é zero linhas, não erro) até ela recarregar.
 *
 * O evento só avisa; quem decide é a consulta: a pessoa ainda é membro ATIVO deste perfil? Se não,
 * `aoPerder` tira ela dali. Para o titular não há o que escutar.
 *
 * O ACESSO POR MÓDULO (03/10/2026) passa pelo mesmo canal: se a pessoa continua na equipe, a
 * consulta traz os níveis de agora, e `aoMudarOAcesso` recebe para quem chama decidir se recarrega
 * (o titular tirou ou deu um módulo com o perfil aberto do outro lado).
 */
export function useVagaNaEquipe(
  artistId: string | null | undefined,
  userId: string | null | undefined,
  souMembro: boolean,
  aoPerder: () => void,
  aoMudarOAcesso?: (niveis: string[]) => void,
): void {
  const ultimo = useRef(aoPerder);
  ultimo.current = aoPerder;
  const mudou = useRef(aoMudarOAcesso);
  mudou.current = aoMudarOAcesso;

  useEffect(() => {
    if (!artistId || !userId || !souMembro || !temTempoReal()) return undefined;
    let vivo = true;
    const conferir = async () => {
      const { data, error } = await supabase
        .from('artist_members')
        .select('id, access_levels')
        .eq('artist_id', artistId)
        .eq('user_id', userId)
        .eq('status', 'active')
        .limit(1);
      // Erro de rede não é expulsão: só a resposta que diz "não é mais membro" tira a pessoa.
      if (!vivo || error) return;
      if (!data?.length) ultimo.current();
      else mudou.current?.(((data[0] as { access_levels?: string[] }).access_levels) ?? []);
    };
    const canal = supabase
      .channel(`vaga-na-equipe:${artistId}:${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes' as never,
        { event: 'UPDATE', schema: 'public', table: 'artist_members', filter: `user_id=eq.${userId}` },
        () => { void conferir(); },
      )
      // A exclusão não se filtra (ver `useTabelaAoVivo`): toda exclusão na tabela faz conferir.
      .on('postgres_changes' as never, { event: 'DELETE', schema: 'public', table: 'artist_members' }, () => { void conferir(); })
      .subscribe();
    return () => {
      vivo = false;
      void supabase.removeChannel(canal);
    };
  }, [artistId, userId, souMembro]);
}
